<template>
    <div class="list-group-item py-1 pe-2 d-flex align-items-center">
        <span class="flex-fill">
            <FormPageContentField :content-id="contentId"/>
        </span>

        <Button @click.native="editContent" class="btn-sm"><i class="bi bi-pencil-square"/></Button>
        <Button @click.native="moveContentUp" class="btn-sm"><i class="bi bi-chevron-up"/></Button>
        <Button @click.native="moveContentDown" class="btn-sm"><i class="bi bi-chevron-down"/></Button>
        <Button @click.native="deleteContent" class="btn-sm"><i class="bi bi-x-lg"/></Button>
    </div>
</template>

<script>
import Button from "../Button.vue"
import FormPageContentField from "../form/FormPageContentField.vue"

export default {
    name: "PageContentContent",
    components: {FormPageContentField, Button},
    props: {
        contentIndex: Number,
        pageContentLinkId: Number
    },
    computed: {
        pageContentData() {
            return this.$store.state.pageContentData.items.find(o => o.id === this.pageContentLinkId)
        },
        contentId() {
            return this.pageContentData?.contentId
        },
        contentArea() {
            return this.pageContentData?.contentArea
        },
        pageId() {
            return this.pageContentData?.pageId
        },
        pageSectionLinkId() {
            return this.pageContentData?.pageSectionLinkId
        },
        pageSections() {
            return this.$store.state.pageSections.items.filter(o => o.pageId === this.pageId)
        },
        contentAreas() {
            return this.pageSections
                .find(o => o.id === this.pageSectionLinkId).areas
                .map(o => o.value)
        },
        areaItems() {
            return this.$store.state.pageContentData.items
                .filter(o => o.pageId === this.pageId && o.contentArea === this.contentArea)
        },
        isSiteTree() {
            return this.$route.name === 'siteTree'
        }
    },
    asyncComputed: {
        content() {
            return this.$store.dispatch('itemData/get', {tableName: 'content', id: this.contentId})
        },
        template() {
            if (this.content) {
                return this.$store.dispatch('itemData/get', {
                    tableName: 'templates_templates',
                    id: this.content.contentType
                })
            }
        }
    },
    methods: {
        editContent() {
            this.$router.push(
                this.isSiteTree
                    ? {name: 'siteTree', params: {pageOrContentId: 'c' + this.pageContentLinkId}}
                    : {name: 'form', params: {component: 'content', id: this.contentId}}
            )
        },
        async moveContentUp() {
            const pageContentLinkId = this.pageContentLinkId
            let pageSectionLinkId = this.pageSectionLinkId
            let contentArea = this.contentArea
            let displayOrder

            if (this.contentIndex === 0) {
                const areaIndex = this.contentAreas.indexOf(this.contentArea)

                // Moving up a section
                if (areaIndex === 0) {
                    const sectionIndex = this.pageSections.findIndex(o => o.id === this.pageSectionLinkId)

                    // First section, do nothing
                    if (sectionIndex === 0) return

                    pageSectionLinkId = this.pageSections[sectionIndex - 1]?.id
                    contentArea = this.pageSections.find(o => o.id === pageSectionLinkId).areas.at(-1).value
                } else {
                    // Moving up an area
                    contentArea = this.contentAreas.at(areaIndex - 1)
                }

                const newAreasItems = this.$store.state.pageContentData.items.filter(o => o.pageId === this.pageId && o.contentArea === contentArea)
                displayOrder = newAreasItems.length + 1

            } else { // Moving up within an area
                displayOrder = this.pageContentData.displayOrder - 1
            }

            await this.$store.dispatch('request/post', {
                url: 'api/move-page-content',
                postData: {pageContentLinkId, pageSectionLinkId, contentArea, displayOrder}
            })

            await this.$store.dispatch('pageContentData/init')
        },
        async moveContentDown() {
            const pageContentLinkId = this.pageContentLinkId
            let pageSectionLinkId = this.pageSectionLinkId
            let contentArea = this.contentArea
            let displayOrder

            const isLastContentInArea = this.contentIndex === this.areaItems.length - 1
            if (isLastContentInArea) {
                const isLastAreaInSection = this.contentArea === this.contentAreas.at(-1)

                // Moving down a section
                if (isLastAreaInSection) {
                    const sectionIndex = this.pageSections.findIndex(o => o.id === this.pageSectionLinkId)
                    pageSectionLinkId = this.pageSections[sectionIndex + 1]?.id

                    // Last section, do nothing
                    if (pageSectionLinkId === undefined) return

                    contentArea = this.pageSections.find(o => o.id === pageSectionLinkId).areas[0].value

                } else { // Moving down an area
                    const areaIndex = this.contentAreas.indexOf(this.contentArea)
                    contentArea = this.contentAreas.at(areaIndex + 1)
                }

                // Whether moving down to a new area or section, it will be placed first
                displayOrder = 1

            } else {
                displayOrder = this.pageContentData.displayOrder + 1
            }

            await this.$store.dispatch('request/post', {
                url: 'api/move-page-content',
                postData: {pageContentLinkId, pageSectionLinkId, contentArea, displayOrder}
            })

            await this.$store.dispatch('pageContentData/init')
        },
        deleteContent() {
            const num = this.$store.state.pageContentData.items.filter(o => o.contentId === this.contentId)?.length

            // Delete the last occurrence of the content
            if (num === 1) {
                this.$store.dispatch('modals/show', {
                    componentName: 'ConfirmModal',
                    obj: {
                        modalTitle: 'Delete content',
                        modalBody: `
                            <p>This is the <strong>only</strong> instance of this content in use.</p>
                            <p class="alert alert-danger">This <strong>cannot</strong> be undone.</p>
                            <p class="mb-0">Are you sure?</p>
                        `,
                        onConfirm: async () => {
                            await this.$store.dispatch('itemData/delete', {tableName: 'content', id: this.contentId})

                            await this.$store.dispatch('pageContentData/init')
                        }
                    }
                })
            } else {
                this.$store.dispatch('modals/show', {
                    componentName: 'ConfirmModal',
                    obj: {
                        modalTitle: 'Detach content',
                        modalBody: `
                            <p class="alert alert-warning">No other references to this content will be affected.</p>
                        `,
                        onConfirm: async () => {
                            await this.$store.dispatch('request/delete', {
                                url: 'api/delete-page-content-link-id/' + this.pageContentLinkId
                            })

                            await this.$store.dispatch('pageContentData/init')
                        }
                    }
                })
            }
        }
    }
}
</script>

<style scoped>

</style>